/**
 * The stack of layers that float over the page: drawers and open dropdown lists.
 *
 * Two jobs, both of which only work if every layer takes part:
 *
 * 1. **Escape closes the top layer only.** A dropdown list open inside a drawer
 *    closes first; the drawer stays. One capture listener on `document` decides,
 *    so no component has to guess whether someone above it wants the key.
 *
 * 2. **A modal layer shuts the rest of the page.** Every child of `<body>` that
 *    does not hold the top modal layer gets `inert` — not clickable, not
 *    focusable, not read out — and `<body>` stops scrolling. A second modal layer
 *    on top takes that over; closing it hands the page back to the one below.
 *
 * Only attributes this module set are ever removed again, so an element that was
 * inert for another reason stays inert.
 */

export interface LayerOptions {
    /** Shut the rest of the page while this layer is the top modal layer. */
    modal: boolean;
    /**
     * The element that stays usable. For a modal layer this must be a child of
     * `<body>` (the drawer renders in a portal there).
     */
    element: HTMLElement | null;
    /** What Escape does while this is the top layer. */
    onEscape: () => void;
}

interface Layer extends LayerOptions {
    id: number;
}

const SKIPPED_TAGS = new Set(['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE', 'NOSCRIPT']);

const stack: Layer[] = [];
const madeInert = new Set<Element>();
let nextId = 1;
let scrollLock: { overflow: string; paddingRight: string } | null = null;

/**
 * Put a layer on top of the stack. Returns the function that takes it off again;
 * call it when the layer closes or unmounts.
 */
export function pushLayer(options: LayerOptions): () => void {
    const layer: Layer = { ...options, id: nextId++ };

    if (stack.length === 0) {
        document.addEventListener('keydown', onKeyDown, true);
    }

    stack.push(layer);
    sync();

    return () => {
        const index = stack.findIndex((candidate) => candidate.id === layer.id);

        if (index === -1) {
            return;
        }

        stack.splice(index, 1);
        sync();

        if (stack.length === 0) {
            document.removeEventListener('keydown', onKeyDown, true);
        }
    };
}

function onKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || event.isComposing) {
        return;
    }

    const top = stack[stack.length - 1];

    if (!top) {
        return;
    }

    event.stopPropagation();
    event.preventDefault();
    top.onEscape();
}

function topModalIndex(): number {
    for (let index = stack.length - 1; index >= 0; index--) {
        if (stack[index]?.modal) {
            return index;
        }
    }

    return -1;
}

function sync(): void {
    const index = topModalIndex();
    const modal = index === -1 ? null : stack[index];
    const keep = [modal, ...stack.slice(index + 1)]
        .map((layer) => layer?.element ?? null)
        .filter((element): element is HTMLElement => element !== null);

    const shouldBeInert = new Set<Element>();

    if (modal?.element) {
        for (const child of Array.from(document.body.children)) {
            if (SKIPPED_TAGS.has(child.tagName)) continue;
            if (keep.some((element) => child.contains(element))) continue;
            if (child.hasAttribute('inert') && !madeInert.has(child)) continue;

            shouldBeInert.add(child);
        }
    }

    for (const element of Array.from(madeInert)) {
        if (!shouldBeInert.has(element)) {
            element.removeAttribute('inert');
            madeInert.delete(element);
        }
    }

    for (const element of shouldBeInert) {
        if (!madeInert.has(element)) {
            element.setAttribute('inert', '');
            madeInert.add(element);
        }
    }

    lockScroll(modal !== null);
}

function lockScroll(locked: boolean): void {
    const body = document.body;

    if (locked && scrollLock === null) {
        const scrollbar = window.innerWidth - document.documentElement.clientWidth;

        scrollLock = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
        body.style.overflow = 'hidden';

        if (scrollbar > 0) {
            body.style.paddingRight = `${scrollbar}px`;
        }
    } else if (!locked && scrollLock !== null) {
        body.style.overflow = scrollLock.overflow;
        body.style.paddingRight = scrollLock.paddingRight;
        scrollLock = null;
    }
}
